import { useEffect, useMemo, useRef, useState } from 'react'
import { Pencil, Trash2, Eye, EyeOff, X, Square, Circle as CircleIcon, AlertTriangle, Check, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { useConfirm } from './ConfirmDialog'
import { loadGoogleMaps, geofenceColor } from '../lib/maps'
import { apiError } from '../lib/api'

const KIND_VALUES = ['boundary', 'no_go', 'restricted']
const kindLabel = (t, k) => (KIND_VALUES.includes(k) ? t(`ops:geofences.kind.${k}`) : k)
const shapeLabel = (t, s) => (s === 'circle' ? t('ops:geofences.shape.circle') : t('ops:geofences.shape.polygon'))

function parseGeometry(g) {
  if (typeof g === 'string') { try { return JSON.parse(g) } catch { return {} } }
  return g || {}
}

export default function GeofencesView() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { mines } = useWS()
  const { mineScope } = useScope()
  const mapRef = useRef(null)
  const gmapRef = useRef(null)
  const overlaysRef = useRef(new Map()) // id da cerca -> overlay no mapa
  const listenersRef = useRef([])
  const previewRef = useRef(null)       // desenho recém-fechado, visível enquanto o painel nomeia
  const modeRef = useRef(null)
  const workRef = useRef({ points: [], markers: [], poly: null, center: null, centerMarker: null })
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.Map)
  const [mode, setMode] = useState(null)
  const [vertexCount, setVertexCount] = useState(0)
  const [fences, setFences] = useState([])
  const [listError, setListError] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [searching, setSearching] = useState(false)
  const [searchMsg, setSearchMsg] = useState(null)
  const geocoderRef = useRef(null)
  const searchMarkerRef = useRef(null)
  const [confirmDialog, ask] = useConfirm()

  useEffect(() => { modeRef.current = mode }, [mode])

  const loadFences = () => {
    fetch('/api/geofences')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(list => { setFences(Array.isArray(list) ? list : []); setListError(null) })
      .catch(err => { setFences([]); setListError(err.message) })
  }

  // Recorte pelo escopo global de mina: 'all' mostra tudo, 'none' só cercas sem
  // mina, id numérico só as cercas daquela mina.
  const scopedFences = useMemo(() => {
    if (mineScope === 'all') return fences
    if (mineScope === 'none') return fences.filter(f => f.mine_id == null)
    return fences.filter(f => Number(f.mine_id) === mineScope)
  }, [fences, mineScope])

  // clearWork remove os overlays temporários do desenho em andamento.
  const clearWork = () => {
    const w = workRef.current
    w.markers.forEach(mk => mk.setMap(null))
    if (w.poly) w.poly.setMap(null)
    if (w.centerMarker) w.centerMarker.setMap(null)
    workRef.current = { points: [], markers: [], poly: null, center: null, centerMarker: null }
    setVertexCount(0)
  }

  // O desenho fechado continua no mapa enquanto o painel pede o nome: antes o clearWork
  // apagava tudo e o operador nomeava uma cerca que já não via.
  const clearPreview = () => {
    if (previewRef.current) { previewRef.current.setMap(null); previewRef.current = null }
  }
  const showPreview = (shape, geometry) => {
    clearPreview()
    const gmaps = window.google?.maps
    const map = gmapRef.current
    if (!gmaps || !map) return
    const style = { map, strokeColor: '#017f96', fillColor: '#017f96', fillOpacity: 0.14, strokeWeight: 2, clickable: false, zIndex: 20 }
    previewRef.current = shape === 'circle'
      ? new gmaps.Circle({ center: geometry.center, radius: geometry.radius_m || 0, ...style })
      : new gmaps.Polygon({ paths: geometry.points, ...style })
  }

  const startMode = (m) => { clearWork(); setMode(m) }
  const cancelDrawing = () => { clearWork(); setMode(null) }
  const closeDraft = () => { clearPreview(); setSaveError(null); setDraft(null) }
  const openDraft = (d) => { setSaveError(null); setDraft(d) }

  const finishPolygon = () => {
    const w = workRef.current
    if (w.points.length < 3) return
    const points = [...w.points]
    clearWork(); setMode(null)
    showPreview('polygon', { points })
    openDraft({ name: '', kind: 'boundary', mine_id: '', alarm_entry: true, alarm_exit: false, shape: 'polygon', geometry: { points } })
  }

  // Edição de uma cerca existente: mesmos campos do desenho, sem mexer na geometria.
  const openEditFence = (f) => {
    setSelectedId(f.id)
    openDraft({
      id: f.id,
      name: f.name || '',
      kind: KIND_VALUES.includes(f.kind) ? f.kind : 'boundary',
      mine_id: f.mine_id == null ? '' : String(f.mine_id),
      alarm_entry: !!f.alarm_entry,
      alarm_exit: !!f.alarm_exit,
      shape: f.shape,
      geometry: parseGeometry(f.geometry),
      active: f.active,
    })
  }

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then(() => { if (!cancelled) setMapsReady(true) }).catch(() => {})
    loadFences()
    return () => { cancelled = true }
  }, [])

  // Desenho próprio (o DrawingManager foi removido da API do Google Maps em v3.65):
  // clique-a-clique para polígono, centro+borda para círculo.
  useEffect(() => {
    if (!mapsReady || !mapRef.current || gmapRef.current) return
    const gmaps = window.google.maps
    const map = new gmaps.Map(mapRef.current, {
      zoom: 14, center: { lat: -23.5505, lng: -46.6333 }, mapTypeId: 'hybrid',
      fullscreenControl: false, streetViewControl: false, mapTypeControl: false,
      draggableCursor: 'crosshair',
    })
    gmapRef.current = map

    const vertexIcon = { path: gmaps.SymbolPath.CIRCLE, scale: 5, fillColor: '#017f96', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1.5 }

    const onClick = (e) => {
      const m = modeRef.current
      if (!m) return
      const w = workRef.current
      const ll = { lat: e.latLng.lat(), lng: e.latLng.lng() }
      if (m === 'polygon') {
        w.points.push(ll)
        w.markers.push(new gmaps.Marker({ position: ll, map, icon: vertexIcon, clickable: false }))
        if (!w.poly) {
          w.poly = new gmaps.Polygon({ map, paths: w.points, strokeColor: '#017f96', fillColor: '#017f96', fillOpacity: 0.12, strokeWeight: 2, clickable: false })
        } else {
          w.poly.setPath(w.points)
        }
        setVertexCount(w.points.length)
      } else if (m === 'circle') {
        if (!w.center) {
          w.center = ll
          w.centerMarker = new gmaps.Marker({ position: ll, map, icon: vertexIcon, clickable: false })
        } else {
          const center = w.center
          const radius = gmaps.geometry.spherical.computeDistanceBetween(new gmaps.LatLng(center), e.latLng)
          clearWork(); setMode(null)
          const geometry = { center, radius_m: Math.round(radius) }
          showPreview('circle', geometry)
          openDraft({ name: '', kind: 'boundary', mine_id: '', alarm_entry: true, alarm_exit: false, shape: 'circle', geometry })
        }
      }
    }
    const listener = gmaps.event.addListener(map, 'click', onClick)
    listenersRef.current.push(listener)
    return () => { gmaps.event.removeListener(listener) }
  }, [mapsReady])

  // Estilo de uma cerca (o destaque da selecionada é só setOptions, sem recriar nada).
  const fenceStyle = (f, sel) => {
    const color = f.color || geofenceColor(f.kind)
    return {
      strokeColor: color, fillColor: color,
      fillOpacity: f.active ? (sel ? 0.24 : 0.12) : 0.04,
      strokeWeight: sel ? 4 : 2, strokeOpacity: f.active ? 0.9 : 0.35,
      zIndex: sel ? 10 : 1,
    }
  }

  // Overlays: cria/remove só o que mudou na lista. Antes um clique numa cerca repintava
  // o mapa inteiro (o efeito dependia do id selecionado).
  useEffect(() => {
    if (!mapsReady || !gmapRef.current) return
    const gmaps = window.google.maps
    const map = gmapRef.current
    const alive = new Set()

    scopedFences.forEach(f => {
      alive.add(f.id)
      const g = parseGeometry(f.geometry)
      const style = fenceStyle(f, f.id === selectedId)
      const existing = overlaysRef.current.get(f.id)
      if (existing) { existing.setOptions(style); return }
      let overlay = null
      if (f.shape === 'circle' && g.center) {
        overlay = new gmaps.Circle({ center: g.center, radius: g.radius_m || 0, map, ...style })
      } else if (Array.isArray(g.points) && g.points.length >= 3) {
        overlay = new gmaps.Polygon({ paths: g.points, map, ...style })
      }
      if (overlay) overlaysRef.current.set(f.id, overlay)
    })

    for (const [id, overlay] of overlaysRef.current) {
      if (alive.has(id)) continue
      overlay.setMap(null)
      overlaysRef.current.delete(id)
    }
    // A geometria de uma cerca não muda depois de criada; o efeito roda de novo só
    // quando a lista ou a seleção mudam, e aí só reaplica estilo.
  }, [scopedFences, mapsReady, selectedId])

  // Cada visita à tela montava um mapa novo e deixava o anterior com os listeners
  // pendurados: solta tudo na saída.
  useEffect(() => () => {
    const gmaps = window.google?.maps
    listenersRef.current.forEach(l => gmaps?.event.removeListener(l))
    listenersRef.current = []
    overlaysRef.current.forEach(o => o.setMap(null))
    overlaysRef.current.clear()
    if (previewRef.current) { previewRef.current.setMap(null); previewRef.current = null }
    const w = workRef.current
    w.markers.forEach(mk => mk.setMap(null))
    if (w.poly) w.poly.setMap(null)
    if (w.centerMarker) w.centerMarker.setMap(null)
    if (searchMarkerRef.current) { searchMarkerRef.current.setMap(null); searchMarkerRef.current = null }
    if (gmapRef.current) { gmaps?.event.clearInstanceListeners(gmapRef.current); gmapRef.current = null }
  }, [])

  // focusFence centraliza e enquadra o mapa na cerca escolhida (círculo pelos
  // limites do raio, polígono pela envoltória dos vértices) e a destaca.
  const focusFence = (f) => {
    const map = gmapRef.current
    if (!map || !window.google?.maps) return
    const gmaps = window.google.maps
    const g = parseGeometry(f.geometry)
    if (f.shape === 'circle' && g.center) {
      map.fitBounds(new gmaps.Circle({ center: g.center, radius: g.radius_m || 0 }).getBounds(), 64)
    } else if (Array.isArray(g.points) && g.points.length) {
      const bounds = new gmaps.LatLngBounds()
      g.points.forEach(p => bounds.extend(p))
      map.fitBounds(bounds, 64)
    }
    setSelectedId(f.id)
  }

  // parseCoords aceita "lat, lng" ou "lat lng" (dentro dos limites geográficos).
  const parseCoords = (s) => {
    const m = s.trim().match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/)
    if (!m) return null
    const lat = parseFloat(m[1]), lng = parseFloat(m[2])
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
    return { lat, lng }
  }

  const dropSearchMarker = (pos) => {
    const gmaps = window.google.maps
    if (searchMarkerRef.current) searchMarkerRef.current.setMap(null)
    searchMarkerRef.current = new gmaps.Marker({ position: pos, map: gmapRef.current, animation: gmaps.Animation.DROP })
  }

  // runSearch: coordenada geográfica vai direto ao ponto; senão geocodifica o
  // endereço/cidade (rua, bairro, cidade) e enquadra o mapa no resultado.
  const runSearch = () => {
    const q = query.trim()
    const map = gmapRef.current
    if (!q || !map) return
    setSearchMsg(null)
    const gmaps = window.google.maps
    const coords = parseCoords(q)
    if (coords) {
      map.panTo(coords); map.setZoom(16); dropSearchMarker(coords)
      return
    }
    if (!geocoderRef.current) geocoderRef.current = new gmaps.Geocoder()
    setSearching(true)
    geocoderRef.current.geocode({ address: q }, (results, status) => {
      setSearching(false)
      if (status === 'OK' && results && results[0]) {
        const geo = results[0].geometry
        if (geo.viewport) map.fitBounds(geo.viewport)
        else { map.panTo(geo.location); map.setZoom(16) }
        dropSearchMarker(geo.location)
      } else if (status === 'ZERO_RESULTS') {
        setSearchMsg(t('ops:geofences.address_not_found'))
      } else {
        setSearchMsg(t('ops:geofences.search_unavailable'))
      }
    })
  }

  const clearSearch = () => {
    setQuery(''); setSearchMsg(null)
    if (searchMarkerRef.current) { searchMarkerRef.current.setMap(null); searchMarkerRef.current = null }
  }

  const saveDraft = async () => {
    if (!draft || !draft.name.trim()) return
    setSaving(true)
    setSaveError(null)
    const body = {
      name: draft.name.trim(),
      kind: draft.kind,
      shape: draft.shape,
      geometry: draft.geometry,
      color: geofenceColor(draft.kind),
      // mine_id é *int64 no backend: string vazia ou "3" viravam 400 e o painel fechava
      // como se tivesse gravado, jogando o desenho fora.
      mine_id: draft.mine_id === '' || draft.mine_id == null ? null : Number(draft.mine_id),
      alarm_entry: draft.kind === 'no_go' ? true : draft.alarm_entry,
      alarm_exit: draft.kind === 'no_go' ? true : draft.alarm_exit,
      active: draft.id ? draft.active !== false : true,
    }
    try {
      const res = await fetch(draft.id ? `/api/geofences/${draft.id}` : '/api/geofences', {
        method: draft.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw await apiError(res)
      closeDraft()
      loadFences()
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (f) => {
    try {
      const res = await fetch(`/api/geofences/${f.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, mine_id: f.mine_id == null ? null : Number(f.mine_id), active: !f.active }),
      })
      if (!res.ok) throw await apiError(res)
    } catch (err) {
      setSaveError(err.message)
    }
    loadFences()
  }

  const removeFence = (f) => {
    ask({
      title: t('ops:geofences.delete_title'),
      message: t('ops:geofences.delete_confirm', { name: f.name }),
      confirmLabel: t('common:actions.delete'),
      danger: true,
      onConfirm: async () => {
        const res = await fetch(`/api/geofences/${f.id}`, { method: 'DELETE' })
        if (!res.ok) throw await apiError(res)
        if (selectedId === f.id) setSelectedId(null)
        loadFences()
      },
    })
  }

  const nameQuery = query.trim().toLowerCase()
  const filtered = nameQuery ? scopedFences.filter(f => (f.name || '').toLowerCase().includes(nameQuery)) : scopedFences

  return (
    <div className="h-full relative">
      {!mapsReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
          <p className="text-sm text-slate-400">{t('ops:map.loading_maps')}</p>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />

      {mapsReady && !draft && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-auto">
          <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur rounded-lg shadow-lg border border-black/5 px-2 py-1.5 flex items-center gap-1.5">
            {mode === null && (
              <>
                <button onClick={() => startMode('polygon')} disabled={!can('engineer')} title={!can('engineer') ? t('admin:perms.needs_engineer') : undefined} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"><Square size={14} /> {t('ops:geofences.shape.polygon')}</button>
                <button onClick={() => startMode('circle')} disabled={!can('engineer')} title={!can('engineer') ? t('admin:perms.needs_engineer') : undefined} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"><CircleIcon size={14} /> {t('ops:geofences.shape.circle')}</button>
              </>
            )}
            {mode === 'polygon' && (
              <>
                <span className="text-xs text-slate-500 dark:text-slate-400 px-1.5">{t('ops:geofences.click_vertices', { count: vertexCount })}</span>
                <button onClick={finishPolygon} disabled={vertexCount < 3} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"><Check size={14} /> {t('common:actions.finish')}</button>
                <button onClick={cancelDrawing} className="text-xs px-2 py-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
              </>
            )}
            {mode === 'circle' && (
              <>
                <span className="text-xs text-slate-500 dark:text-slate-400 px-1.5">{t('ops:geofences.click_center_edge')}</span>
                <button onClick={cancelDrawing} className="text-xs px-2 py-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="absolute top-3 left-3 bottom-3 w-72 flex flex-col pointer-events-none">
        <div className="pointer-events-auto bg-white/90 dark:bg-slate-900/90 backdrop-blur rounded-xl shadow-lg border border-black/5 overflow-hidden flex flex-col max-h-full">
          <div className="p-2 border-b border-black/5 shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runSearch() }}
                placeholder={t('ops:geofences.search_placeholder')}
                title={t('ops:geofences.search_hint')}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 pl-8 pr-8 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none" />
              {query && (
                <button onClick={clearSearch} title={t('common:actions.clear')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                  <X size={13} />
                </button>
              )}
            </div>
            {searching && <p className="mt-1 px-0.5 text-[10px] text-slate-400">{t('ops:geofences.searching')}</p>}
            {searchMsg && <p className="mt-1 px-0.5 text-[10px] text-status-down">{searchMsg}</p>}
          </div>
          <div className="px-3 py-2 border-b border-black/5 text-xs font-semibold text-brand-600 shrink-0">
            {t('ops:geofences.fences')} ({filtered.length}{nameQuery && filtered.length !== scopedFences.length ? ` ${t('ops:geofences.of_total', { total: scopedFences.length })}` : ''})
          </div>
          <div className="overflow-y-auto scrollbar-thin divide-y divide-black/5">
            {filtered.map(f => {
              const color = f.color || geofenceColor(f.kind)
              const sel = f.id === selectedId
              return (
                <div key={f.id} onClick={() => focusFence(f)} title={t('ops:map.center_on_map')}
                  className={`px-3 py-2.5 cursor-pointer transition-colors ${f.active ? '' : 'opacity-60'} ${sel ? 'bg-brand-50 dark:bg-brand-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{f.name}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white" style={{ backgroundColor: color }}>
                          {kindLabel(t, f.kind)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                          {f.shape === 'circle' ? <CircleIcon size={11} /> : <Square size={11} />}
                          {shapeLabel(t, f.shape)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); toggleActive(f) }} title={f.active ? t('ops:geofences.deactivate') : t('ops:geofences.activate')}
                        className="p-1 text-slate-400 dark:text-slate-500 hover:text-brand-600">
                        {f.active ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); openEditFence(f) }} title={t('common:actions.edit')}
                        className="p-1 text-slate-400 dark:text-slate-500 hover:text-brand-600">
                        <Pencil size={15} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeFence(f) }} title={t('common:actions.delete')}
                        className="p-1 text-slate-400 dark:text-slate-500 hover:text-status-down">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
            {filtered.length === 0 && (
              listError ? (
                <div className="px-3 py-6 text-center">
                  <p className="text-xs text-status-down">{t('common:states.error')}</p>
                  <button onClick={loadFences} className="mt-2 text-xs text-brand-600 hover:underline">{t('common:actions.retry')}</button>
                </div>
              ) : (
                <p className="px-3 py-6 text-xs text-slate-500 dark:text-slate-400 text-center">
                  {scopedFences.length === 0
                    ? t('ops:geofences.none_in_scope')
                    : t('ops:geofences.none_by_name')}
                </p>
              )
            )}
          </div>
        </div>
      </div>

      <div className={`absolute top-3 right-3 bottom-3 w-80 transition-transform duration-300 ${draft ? 'translate-x-0' : 'translate-x-[120%]'}`}>
        <div className="h-full bg-white/90 dark:bg-slate-900/90 backdrop-blur rounded-xl shadow-lg border border-black/5 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 text-brand-600">
              <Pencil size={16} />
              <span className="text-sm font-semibold">{draft?.id ? t('ops:geofences.edit_fence') : t('ops:geofences.new_fence')}</span>
            </div>
            <button onClick={closeDraft} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
              <X size={16} />
            </button>
          </div>

          {draft && (
            <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('ops:geofences.name_label')}</span>
                <input type="text" autoFocus value={draft.name}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                  placeholder={t('ops:geofences.name_placeholder')} />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('ops:geofences.type_label')}</span>
                <select value={draft.kind}
                  onChange={e => setDraft({ ...draft, kind: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none">
                  {KIND_VALUES.map(v => <option key={v} value={v}>{t(`ops:geofences.kind.${v}`)}</option>)}
                </select>
                <span className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="w-3 h-3 rounded-sm ring-1 ring-black/10" style={{ backgroundColor: geofenceColor(draft.kind) }} />
                  {t('ops:geofences.auto_color')}
                </span>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('ops:geofences.mine_optional')}</span>
                <select value={draft.mine_id}
                  onChange={e => setDraft({ ...draft, mine_id: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none">
                  <option value="">{t('ops:geofences.all_option')}</option>
                  {(mines || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>

              {draft.kind === 'no_go' ? (
                <div className="flex items-start gap-2 rounded-lg bg-status-down/10 border border-status-down/20 px-3 py-2.5 text-[11px] text-status-down">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>{t('ops:geofences.no_go_warning')}</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <input type="checkbox" checked={draft.alarm_entry}
                      onChange={e => setDraft({ ...draft, alarm_entry: e.target.checked })}
                      className="rounded border-slate-300 dark:border-slate-600 text-brand-600 focus:ring-brand-500" />
                    {t('ops:geofences.alarm_entry')}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <input type="checkbox" checked={draft.alarm_exit}
                      onChange={e => setDraft({ ...draft, alarm_exit: e.target.checked })}
                      className="rounded border-slate-300 dark:border-slate-600 text-brand-600 focus:ring-brand-500" />
                    {t('ops:geofences.alarm_exit')}
                  </label>
                </div>
              )}

              {saveError && (
                <p className="flex items-start gap-1.5 text-[11px] text-status-down">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {saveError}
                </p>
              )}
            </div>
          )}

          <div className="px-4 py-3 border-t border-black/5 flex items-center gap-2 shrink-0">
            <button onClick={closeDraft}
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
              {t('common:actions.cancel')}
            </button>
            <button onClick={saveDraft} disabled={saving || !draft?.name.trim()}
              className="flex-1 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
              {saving ? t('ops:geofences.saving') : t('common:actions.confirm')}
            </button>
          </div>
        </div>
      </div>
      {confirmDialog}
    </div>
  )
}
